// src/lib/glba/glbaPreferencesApi.ts

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { GLBAPreferences } from '../../types/glbaPreferences';

/**
 * Get GLBA preferences for a user
 */
export async function getGLBAPreferences(userId: string): Promise<GLBAPreferences | null> {
  try {
    const prefsDocRef = doc(db, 'glbaPreferences', userId);
    const prefsDoc = await getDoc(prefsDocRef);
    
    if (prefsDoc.exists()) {
      return prefsDoc.data() as GLBAPreferences;
    }
    
    return null;
  } catch (error: any) {
    console.error('Error fetching GLBA preferences:', error);
    throw new Error(`Failed to fetch GLBA preferences: ${error.message}`);
  }
}

/**
 * Create or update GLBA preferences for a user
 */
export async function updateGLBAPreferences(
  userId: string,
  preferences: Partial<GLBAPreferences>
): Promise<void> {
  try {
    const prefsDocRef = doc(db, 'glbaPreferences', userId);
    
    // Try to get existing preferences, but don't fail if document doesn't exist
    let existingPrefs: GLBAPreferences | null = null;
    try {
      existingPrefs = await getGLBAPreferences(userId);
    } catch (error: any) {
      // If document doesn't exist, that's fine - we'll create it
      console.log('No existing GLBA preferences found, will create new document');
    }
    
    const now = Timestamp.now();
    const nextAnnualDue = new Date();
    nextAnnualDue.setFullYear(nextAnnualDue.getFullYear() + 1);
    
    // Build preferences object, only including defined values
    const updatedPrefs: any = {
      userId,
      optOutInformationSharing: preferences.optOutInformationSharing ?? existingPrefs?.optOutInformationSharing ?? false,
      optOutAffiliateMarketing: preferences.optOutAffiliateMarketing ?? existingPrefs?.optOutAffiliateMarketing ?? false,
      updatedAt: now,
      nextAnnualNoticeDue: preferences.nextAnnualNoticeDue ?? existingPrefs?.nextAnnualNoticeDue ?? Timestamp.fromDate(nextAnnualDue),
    };
    
    // Only include optional fields if they have values (not undefined)
    if (preferences.initialNoticeDeliveredAt !== undefined) {
      updatedPrefs.initialNoticeDeliveredAt = preferences.initialNoticeDeliveredAt;
    } else if (existingPrefs?.initialNoticeDeliveredAt !== undefined) {
      updatedPrefs.initialNoticeDeliveredAt = existingPrefs.initialNoticeDeliveredAt;
    }
    
    if (preferences.lastAnnualNoticeDeliveredAt !== undefined) {
      updatedPrefs.lastAnnualNoticeDeliveredAt = preferences.lastAnnualNoticeDeliveredAt;
    } else if (existingPrefs?.lastAnnualNoticeDeliveredAt !== undefined) {
      updatedPrefs.lastAnnualNoticeDeliveredAt = existingPrefs.lastAnnualNoticeDeliveredAt;
    }
    
    // Use setDoc with merge to create or update
    await setDoc(prefsDocRef, updatedPrefs, { merge: true });
  } catch (error: any) {
    console.error('Error updating GLBA preferences:', error);
    throw new Error(`Failed to update GLBA preferences: ${error.message}`);
  }
}

/**
 * Mark initial privacy notice as delivered
 */
export async function markInitialNoticeDelivered(userId: string): Promise<void> {
  try {
    const now = Timestamp.now();
    const nextAnnualDue = new Date();
    nextAnnualDue.setFullYear(nextAnnualDue.getFullYear() + 1);
    
    await updateGLBAPreferences(userId, {
      initialNoticeDeliveredAt: now,
      nextAnnualNoticeDue: Timestamp.fromDate(nextAnnualDue),
    });
  } catch (error: any) {
    console.error('Error marking initial notice as delivered:', error);
    throw error;
  }
}

/**
 * Mark annual privacy notice as delivered
 */
export async function markAnnualNoticeDelivered(userId: string): Promise<void> {
  try {
    const now = Timestamp.now();
    const nextAnnualDue = new Date();
    nextAnnualDue.setFullYear(nextAnnualDue.getFullYear() + 1);
    
    await updateGLBAPreferences(userId, {
      lastAnnualNoticeDeliveredAt: now,
      nextAnnualNoticeDue: Timestamp.fromDate(nextAnnualDue),
    });
  } catch (error: any) {
    console.error('Error marking annual notice as delivered:', error);
    throw error;
  }
}
